import React from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import { MdRocketLaunch, MdArrowDownward } from 'react-icons/md';
import { glassStyle } from './themes';

/**
 * Hero: what this is, who it's for, and one thing to do next.
 *
 * The strip below the CTA used to carry invented traction numbers
 * ("2,400+ Brands Launched", "180K+ NFTs Minted"). Numbers like that are
 * checkable, and a visitor who discovers one is false stops believing the
 * rest of the page. These are capability claims instead — each one is
 * something the product actually does, and each is expanded on further down.
 */
const PROOF = [
  { value: 'Your domain', label: 'Custom domains with automatic HTTPS' },
  { value: 'Your Stripe', label: 'Take payments through your own account' },
  { value: 'Your data',   label: 'Export any time — no lock-in' },
];

export default function Hero({ t, onSignupClick }) {
  const { isMobile } = useBreakpoint();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div style={{
      // No forced full-screen height on a phone. Mobile browsers count their
      // collapsing toolbar in vh, so 100vh is taller than the visible screen
      // and pushes the hero's own call to action below the fold on first
      // paint — the opposite of what a hero is for. Letting the content set
      // the height avoids that without relying on svh, which older mobile
      // Safari does not understand (and an unsupported unit here would make
      // the whole declaration invalid, not fall back).
      minHeight: isMobile ? 'auto' : '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(90px, 14vh, 120px) clamp(16px, 5vw, 24px) 40px',
      position: 'relative', zIndex: 1,
    }}>
      <div style={{ maxWidth: 860, textAlign: 'center' }}>
        <div style={{ display: 'inline-block', padding: '4px 16px', borderRadius: 999, background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.35)', color: '#A78BFA', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', marginBottom: 24 }}>
          STOREFRONT · PAGES · PAYMENTS · NFTs
        </div>
        <h1 style={{ fontSize: 'clamp(38px, 6vw, 74px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-2px', color: t.text, margin: '0 0 24px' }}>
          Run the whole brand.{' '}
          <span style={{ background: 'linear-gradient(to right, #A78BFA, #60A5FA, #34D399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>From one place.</span>
        </h1>
        <p style={{ fontSize: 'clamp(15px, 2vw, 20px)', color: t.textSub, maxWidth: 620, margin: '0 auto 36px', lineHeight: 1.7 }}>
          Build the site, sell the products, take the payments, send the emails and
          mint the collection — on your own domain, under your own Stripe account.
          Run several brands from one login, each with its own team.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <button
            onClick={onSignupClick}
            style={{ height: 54, padding: '0 32px', borderRadius: 14, background: 'linear-gradient(135deg, #7C3AED, #4F46E5)', color: '#fff', fontWeight: 700, fontSize: 16, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all 0.25s' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(124,58,237,0.5)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <MdRocketLaunch size={18} /> Create your brand — free
          </button>
          {/* Was a "Watch Demo" button with no handler at all. Until a demo
              exists, send people to the part of the page that answers the
              same question. */}
          <button
            onClick={() => scrollTo('features')}
            style={{ height: 54, padding: '0 32px', borderRadius: 14, background: t.surface, border: `1px solid ${t.border}`, color: t.text, fontWeight: 600, fontSize: 16, cursor: 'pointer', transition: 'all 0.25s', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.surface; }}
          >
            <MdArrowDownward size={17} /> See what's included
          </button>
        </div>
        <p style={{ color: t.textMuted, fontSize: 13, margin: '0 0 48px' }}>
          No credit card to start · Connect a domain whenever you're ready
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          {PROOF.map((s) => (
            <div key={s.value} style={{ ...glassStyle(t), padding: '16px 22px', textAlign: 'left', maxWidth: 260, flex: '1 1 220px' }}>
              <div style={{ color: t.text, fontWeight: 700, fontSize: 16 }}>{s.value}</div>
              <div style={{ color: t.textMuted, fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
